import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { osApi } from '../../services/osApi';
import { useDataTransformation } from './useDataTransformation';
import { useVersionControl } from './useVersionControl';
import { useFileManagementContext } from '../../context/FileManagementContext';
import { useDemand, ACTIONS } from '../../context/DemandContext';

/**
 * 🎯 需求單數據載入 Hook
 * 處理需求單數據的查詢和載入邏輯，包括檔案管理和版本控制
 */
export const useDemandDataLoader = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { dispatch } = useDemand();
  const { transformFromApiFormat } = useDataTransformation();
  const { setVersionData } = useVersionControl();
  const { actions: fileActions } = useFileManagementContext();
  const queryClient = useQueryClient();

  /**
   * 根據需求單 ID 載入數據
   * @param {string} demandId - 需求單編號
   * @param {Object} options - 載入選項
   * @param {boolean} options.includeFileManagement - 是否包含檔案管理邏輯
   * @param {boolean} options.includeVersionControl - 是否包含版本控制邏輯
   * @returns {Promise<boolean>} 是否載入成功
   */
  const loadDemandData = useCallback(async (demandId, options = {}) => {
    const {
      includeFileManagement = true,
      includeVersionControl = true
    } = options;

    if (!demandId) {
      console.warn('[useDemandDataLoader] No demand ID provided');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 使用現有的 osApi 和 queryClient，參考 Layout.tsx 的邏輯
      const result = await queryClient.fetchQuery({
        queryKey: ['os_item', demandId],
        queryFn: () => osApi.getItemById(demandId),
        staleTime: 0, // 強制重新查詢
      });

      console.log('[useDemandDataLoader] API response:', result);

      if (!result?.data) {
        setError('查無資料');
        message.error('查無資料');
        return false;
      }

      // 使用 transformFromApiFormat 轉換數據
      const transformedData = await transformFromApiFormat(result.data);
      
      if (!transformedData) {
        setError('數據轉換失敗');
        message.error('數據載入失敗，請稍後再試');
        return false;
      }

      console.log('[useDemandDataLoader] Transformed data:', transformedData);

      // 檔案管理：批量導入 blobs 到 FileManagementContext
      if (includeFileManagement && transformedData.sourceData?.blobs) {
        transformedData.sourceData.blobs.forEach((blob) => {
          fileActions.addServerFile(blob);
        });
        console.log('[useDemandDataLoader] Files added to FileManagementContext');
      }

      // 版本控制：設置版本數據
      if (includeVersionControl && transformedData.sourceData) {
        setVersionData({
          sourceItem: transformedData.sourceData.version,
          sourceBlob: transformedData.sourceData.blobs,
          sourceJsonFileId: transformedData.sourceData.jsonFileId,
          selectedVersion: transformedData.sourceData.version.length - 1
        });
        console.log('[useDemandDataLoader] Version control data set');
      }

      // 將轉換後的數據載入到 DemandContext
      dispatch({
        type: ACTIONS.SET_CEREMONY_DATA,
        payload: {
          basic: transformedData.basic,
          ceremony: transformedData.ceremony,
          notes: transformedData.notes,
          price: transformedData.price,
          live: transformedData.live
        }
      });

      console.log('[useDemandDataLoader] 需求單數據載入成功');
      return true;

    } catch (error) {
      console.error('[useDemandDataLoader] Error loading demand data:', error);
      
      // 根據錯誤類型顯示不同的錯誤訊息
      if (error?.response?.status === 404) {
        setError('找不到對應的需求單');
        message.error('找不到對應的需求單，請檢查編號是否正確');
      } else if (error?.response?.status === 403) {
        setError('無權限訪問此需求單');
        message.error('無權限訪問此需求單');
      } else {
        setError(error.message || '載入失敗');
        message.error('載入需求單數據時發生錯誤，請稍後再試');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, transformFromApiFormat, setVersionData, fileActions, queryClient]);

  /**
   * 從 localStorage 獲取當前登入的需求單編號並載入數據
   * @param {Object} options - 載入選項
   * @returns {Promise<boolean>} 是否載入成功
   */
  const loadCurrentMemberData = useCallback(async (options = {}) => {
    try {
      const memberInfo = localStorage.getItem('memberInfo');
      if (!memberInfo) {
        setError('未找到會員資訊');
        message.error('請先登入');
        return false;
      }

      const { projectName } = JSON.parse(memberInfo);
      if (!projectName) {
        setError('會員資訊中缺少需求單編號');
        message.error('會員資訊不完整，請重新登入');
        return false;
      }

      return await loadDemandData(projectName, options);
    } catch (error) {
      console.error('[useDemandDataLoader] Error loading current member data:', error);
      setError('載入會員數據失敗');
      message.error('載入會員數據失敗，請重新登入');
      return false;
    }
  }, [loadDemandData]);

  /**
   * 清除錯誤狀態
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    loadDemandData,
    loadCurrentMemberData,
    clearError
  };
}; 